import {
  ActionIcon,
  Badge,
  Box,
  Group,
  Highlight,
  Image,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconMessageCircle, IconSearch, IconTrash } from '@tabler/icons-react';
import Fuse from 'fuse.js';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getCharacterImagePath } from '../../lib/character-utils';
import type { ChatMessage, Conversation } from '../../lib/chat-db';
import { formatRelativeDate } from '../../lib/chat-utils';

interface ConversationModalProps {
  opened: boolean;
  onClose: () => void;
  conversations: Conversation[];
  currentConversationId: string;
  onSelectConversation: (conversationId: string) => void;
  onDeleteConversation: (conversationId: string) => void;
}

interface SearchableConversation extends Conversation {
  allMessages: ChatMessage[];
}

// type SearchResult = FuseResult<SearchableConversation>;

export const ConversationModal = React.memo<ConversationModalProps>(
  ({
    opened,
    onClose,
    conversations,
    currentConversationId,
    onSelectConversation,
    onDeleteConversation,
  }) => {
    // Search functionality
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch] = useDebouncedValue(searchQuery, 300);
    const [searchableConversations, setSearchableConversations] = useState<
      SearchableConversation[]
    >([]);
    const [, setIsSearching] = useState(false);

    // Prepare searchable data
    useEffect(() => {
      const prepareSearchData = async () => {
        if (conversations.length === 0) {
          setSearchableConversations([]);
          return;
        }

        try {
          setIsSearching(true);
          const { chatDB } = await import('../../lib/chat-db');
          await chatDB.init();

          const searchable = await Promise.all(
            conversations.map(async (conv) => {
              const messages = await chatDB.getMessages(conv.id);
              return {
                ...conv,
                allMessages: messages,
              };
            })
          );
          setSearchableConversations(searchable);
        } catch (error) {
          console.error('Failed to prepare search data:', error);
          setSearchableConversations(conversations.map((conv) => ({ ...conv, allMessages: [] })));
        } finally {
          setIsSearching(false);
        }
      };

      if (opened && conversations.length > 0) {
        prepareSearchData();
      }
    }, [opened, conversations]);

    // Fuse.js configuration
    const fuse = useMemo(() => {
      if (searchableConversations.length === 0) return null;

      return new Fuse(searchableConversations, {
        keys: [
          { name: 'title', weight: 0.7 },
          { name: 'allMessages.content', weight: 0.5 },
          { name: 'allMessages.reasoning', weight: 0.3 },
        ],
        includeMatches: true,
        includeScore: true,
        threshold: 0.4,
        minMatchCharLength: 2,
      });
    }, [searchableConversations]);

    // Search results
    const searchResults = useMemo(() => {
      if (!fuse || !debouncedSearch.trim()) {
        return [];
      }

      const results = fuse.search(debouncedSearch);
      return results.slice(0, 10); // Limit to 10 results
    }, [fuse, debouncedSearch]);

    // Determine which conversations to display
    const displayedConversations = useMemo(() => {
      if (!debouncedSearch.trim()) {
        return conversations;
      }
      return searchResults.map((result) => result.item);
    }, [debouncedSearch, conversations, searchResults]);

    // Clear search when modal closes
    useEffect(() => {
      if (!opened) {
        setSearchQuery('');
      }
    }, [opened]);

    const handleDeleteClick = (e: React.MouseEvent, conversationId: string) => {
      e.stopPropagation();
      onDeleteConversation(conversationId);
    };

    const handleSelectConversation = (conversationId: string) => {
      onSelectConversation(conversationId);
      onClose();
    };

    // Render conversation with highlighting
    const renderConversation = useCallback(
      (conversation: Conversation) => {
        // Find the search result for this conversation to access matches
        const searchResult = searchResults.find((result) => result.item.id === conversation.id);
        const titleMatch = searchResult?.matches?.find((m) => m.key === 'title');
        const messageMatches = searchResult?.matches?.filter((m) => m.key?.includes('allMessages'));
        const hasMatches =
          debouncedSearch.trim() && (titleMatch || (messageMatches && messageMatches.length > 0));

        return (
          <Paper
            key={conversation.id}
            withBorder
            p="md"
            style={{
              cursor: 'pointer',
              minHeight: '80px',
              backgroundColor:
                conversation.id === currentConversationId
                  ? 'var(--mantine-color-default-hover)'
                  : 'transparent',
              borderColor:
                conversation.id === currentConversationId
                  ? 'var(--mantine-color-default-border)'
                  : undefined,
              transition: 'background-color 0.2s ease, border-color 0.2s ease',
            }}
            onClick={() => handleSelectConversation(conversation.id)}
            onMouseEnter={(e) => {
              if (conversation.id !== currentConversationId) {
                e.currentTarget.style.backgroundColor = 'var(--mantine-color-default-hover)';
              }
            }}
            onMouseLeave={(e) => {
              if (conversation.id !== currentConversationId) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <Group align="flex-start" gap="md" wrap="nowrap">
              {/* Character image or icon */}
              {conversation.systemPromptType && conversation.systemPromptType !== 'custom' ? (
                <Image
                  src={getCharacterImagePath(conversation.systemPromptType)}
                  alt="Character"
                  w={32}
                  h={32}
                  radius="md"
                  fallbackSrc="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjMyIiBoZWlnaHQ9IjMyIiByeD0iNiIgZmlsbD0iIzg4ODg4OCIvPgo8cGF0aCBkPSJNMTYgMTBDMTcuNDcyNyAxMCAxOC42NjY3IDExLjE5NCAxOC42NjY3IDEyLjY2NjdDMTguNjY2NyAxNC4xMzkzIDE3LjQ3MjcgMTUuMzMzMyAxNiAxNS4zMzMzQzE0LjUyNzMgMTUuMzMzMyAxMy4zMzMzIDE0LjEzOTMgMTMuMzMzMyAxMi42NjY3QzEzLjMzMzMgMTEuMTk0IDE0LjUyNzMgMTAgMTYgMTBaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMTYgMjRDMTguNjY2NyAyNCAyMS4zMzMzIDIyLjQgMjEuMzMzMyAxOS43MzMzQzIxLjMzMzMgMTguNjY2NyAxNy43NzQ3IDE3LjA2NjcgMTYgMTcuMDY2N0MxNC4yMjUzIDE3LjA2NjcgMTAuNjY2NyAxOC42NjY3IDEwLjY2NjcgMTkuNzMzM0MxMC42NjY3IDIyLjQgMTMuMzMzMyAyNCAxNiAyNFoiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPg=="
                  style={{ flexShrink: 0 }}
                />
              ) : conversation.systemPromptType === 'custom' ? (
                <Box
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 6,
                    backgroundColor: 'var(--mantine-color-default-hover)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    fontSize: 16,
                  }}
                >
                  ✏️
                </Box>
              ) : (
                <IconMessageCircle size={32} style={{ marginTop: 2, flexShrink: 0 }} />
              )}

              <Box style={{ flex: 1, overflow: 'hidden' }}>
                {titleMatch ? (
                  <Text size="sm" fw={500} truncate>
                    <Highlight highlight={debouncedSearch}>{conversation.title}</Highlight>
                  </Text>
                ) : (
                  <Text size="sm" fw={500} truncate>
                    {conversation.title}
                  </Text>
                )}

                <Group gap="xs" mt={4}>
                  <Text size="xs" c="dimmed">
                    {conversation.lastSelectedModel || 'No model'}
                  </Text>
                  <Text size="xs" c="dimmed">
                    •
                  </Text>
                  <Text size="xs" c="dimmed">
                    {formatRelativeDate(conversation.createdAt)}
                  </Text>
                  {hasMatches && (
                    <>
                      <Text size="xs" c="dimmed">
                        •
                      </Text>
                      <Badge size="xs" variant="light" color="blue">
                        Match
                      </Badge>
                    </>
                  )}
                </Group>

                {/* Show message snippets for search matches */}
                {messageMatches && messageMatches.length > 0 && (
                  <Box mt={6}>
                    {messageMatches.slice(0, 2).map((match, index) => {
                      if (!match.value) return null;
                      const snippet =
                        match.value.length > 80
                          ? `${match.value.substring(0, 80)}...`
                          : match.value;

                      return (
                        <Text
                          key={index}
                          size="xs"
                          c="dimmed"
                          style={{ fontStyle: 'italic', lineHeight: 1.4 }}
                        >
                          <Highlight highlight={debouncedSearch}>{snippet}</Highlight>
                        </Text>
                      );
                    })}
                  </Box>
                )}

                {conversation.messageCount > 0 && (
                  <Text size="xs" c="dimmed" mt={4}>
                    {conversation.messageCount} message{conversation.messageCount !== 1 ? 's' : ''}
                  </Text>
                )}
              </Box>

              <Tooltip label="Delete conversation">
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="red"
                  onClick={(e) => handleDeleteClick(e, conversation.id)}
                  style={{ flexShrink: 0 }}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Paper>
        );
      },
      [
        currentConversationId,
        debouncedSearch,
        searchResults,
        handleDeleteClick,
        handleSelectConversation,
      ]
    );

    return (
      <Modal opened={opened} onClose={onClose} title="Recent Conversations" size="md" centered>
        <Box>
          {/* Search Section */}
          <TextInput
            placeholder="Search your conversations..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.currentTarget.value)}
            mb="md"
            data-autofocus
          />

          {conversations.length === 0 ? (
            <Box ta="center" py="xl">
              <Text size="sm" c="dimmed">
                No conversations yet
              </Text>
              <Text size="xs" c="dimmed" mt="xs">
                Start a new chat to begin
              </Text>
            </Box>
          ) : (
            <>
              {/* Search Results Info */}
              {debouncedSearch.trim() && (
                <Text size="sm" c="dimmed" mb="sm">
                  {displayedConversations.length > 0
                    ? `Found ${displayedConversations.length} result${displayedConversations.length === 1 ? '' : 's'}`
                    : `No conversations found matching "${debouncedSearch}"`}
                </Text>
              )}

              <ScrollArea h={450}>
                <Stack gap="xs">
                  {displayedConversations.map((conversation) => renderConversation(conversation))}
                </Stack>
              </ScrollArea>
            </>
          )}
        </Box>
      </Modal>
    );
  }
);

ConversationModal.displayName = 'ConversationModal';
